import { useRecoilValue } from 'recoil';

import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { LoadingIndicator } from '@/components/ui/loading/indicator';
import { getMinRate } from '@/lib/models/rates/get-min-rate';
import { getTotalRate } from '@/lib/models/rates/get-total-rate';
import { SpotRateModel } from '@/lib/models/rates/types';
import { spotRateSearchState } from '@/state/rates';
import { formatPrice } from '@/utils/format';
import { tv } from '@/utils/styles';

interface RateBadgeProps {
  rates: SpotRateModel[];
}

const RateBadge = ({ rates }: RateBadgeProps) => {
  const { isSearching } = useRecoilValue(spotRateSearchState);

  const lowestRate = getMinRate(rates);
  const totalRate = getTotalRate(lowestRate);
  const hasRate = !isSearching && Boolean(lowestRate);

  const { base, loading, primaryText, subText } = styles();

  if (isSearching) {
    return (
      <div className={base()}>
        <span className={loading()}>
          <LoadingIndicator className="mr-0" darkTheme />
        </span>
      </div>
    );
  }

  return (
    <div className={base({ hasRate })}>
      {totalRate?.value ? (
        <span>
          <Text className={primaryText()}>{formatPrice(Math.round(totalRate.value), totalRate.currency, 1)}</Text>
          <Text className={subText()}>Per TEU</Text>
        </span>
      ) : (
        <Tooltip content="Spot rate not available for this service">
          <Text className={primaryText({ className: 'text-grey-500' })}>--</Text>
          <Text className={subText()}>Per TEU</Text>
        </Tooltip>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative z-4 -my-0.5 flex min-h-10 min-w-16 flex-col justify-center rounded-md bg-grey-50 px-2 py-1',
    loading: 'absolute bottom-0 left-0 right-0 top-0 grid place-items-center',
    primaryText: 'm-0 text-baseSm font-medium leading-4 text-text-primary',
    subText: 'm-0 mt-1 text-xs leading-3 text-grey-500',
  },
  variants: {
    hasRate: {
      true: 'bg-softPurple',
    },
  },
});

export { RateBadge };
